/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

/**
 * Utility pro práci s HTTP hlavičkami ve Fetch API
 *
 * Zdroje:
 * - [Headers WHATWG](https://fetch.spec.whatwg.org/#headers-class)
 * - [Headers MDN](https://developer.mozilla.org/en-US/docs/Web/API/Headers)
 */
class HeadersUtil {
  /**
   * Sloučí několik objektů s hlavičkami do jednoho.
   * Funguje to obdobně jako `Object.assign`,
   * kde klíče objektu nahrazují názvy hlaviček.
   * Narozdíl od `Object.assign` je metoda imutabilní (chybí `target`)
   * a není podporováno `undefined` a `null` na vstupu.
   *
   * Zdroje:
   * -[Object.assign MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
   * @param {...Headers} sources
   * @returns {Headers}
   */
  merge(...sources) {
    const target = new Headers();
    sources.forEach((source) => {
      this.toEntries(source).forEach(([name, value]) => {
        target.set(name, value);
      });
    });
    return target;
  }

  /**
   * Sloučí několik inicializačních objektů hlaviček do jednoho.
   * Funguje to obdobně jako `Object.assign`,
   * kde klíče objektu nahrazují názvy hlaviček.
   * Narozdíl od `Object.assign` je metoda imutabilní (chybí `target`)
   * a není podporováno `undefined` a `null` na vstupu.
   *
   * Zdroje:
   * -[Object.assign MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
   * @param {...HeadersInit} sources
   * @returns {HeadersInit}
   */
  mergeInit(...sources) {
    // Použije se převod na Headers objekty,
    // aby byl jednotně ošetřeny všechny možné typy HeadersInit.
    const headers = sources.map((source) => new Headers(source));
    // Objekty se sloučí do jednoho Headers objektu.
    const merged = this.merge(...headers);
    // Výsledný objekt se převede zpět na HeadersInit objekt.
    return this.toObject(merged);
  }

  /**
   * Převede hlavičky na dvojice klíč:hodnota, ve kterých jsou klíče
   * názvy hlaviček a hodnoty jsou hodnoty hlaviček.
   * Implementace nevyžaduje nativní podporu iterátorů a symbolů.
   *
   * Zdroje:
   * -[Object.entries MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/entries)
   * @param {Headers} headers
   * @returns {Array.<Array.<string>>}
   */
  toEntries(headers) {
    // Musí se použít stavová proměnná s hlavičkami,
    // protože nelze předem určit jejich počet.
    const entries = [];
    // Vytvoří se iterátor hlaviček.
    const iterator = headers.entries();
    // Spustí se nekonečný cyklus.
    // Dle Fetch API specifikace je hlaviček konečný počet.
    while (true) {
      // Načte se hlavička.
      const result = iterator.next();
      // Pokud iterátor skončil, ukončí se i cyklus.
      if (result.done) {
        break;
      }
      // Vložení hodnoty [key, value] do seznamu hlaviček.
      entries.push(result.value);
    }
    return entries;
  }

  /**
   * Převede hlavičky na prostý objekt, ve kterém jsou klíče názvy hlaviček
   * a hodnoty jsou hodnoty hlaviček.
   * Implementace nevyžaduje nativní podporu iterátorů a symbolů.
   * @param {Headers} headers
   * @returns {Object.<string, string>}
   */
  toObject(headers) {
    // Převod seznamu na objekt {key: value}.
    // Dle Fetch API specifikace se více stejných hlaviček spojí
    // do jednoho řetězce, nemůže tedy dojít k výskytu dvou stejných klíčů
    // a ke kolizi při převodu na objekt.
    const entries = this.toEntries(headers);
    return Object.fromEntries(entries);
  }
}

export default HeadersUtil;
